/**
 * Internationalization (i18n) module
 * Handles language switching and URL management
 */

/**
 * Gets the language code from URL query parameters
 * @param {Location} location - The location object
 * @returns {string|null} 'en', 'lo', or null if not specified
 */
export function getLangFromUrl(location = window.location) {
  try {
    const params = new URLSearchParams(location.search);
    const value = (params.get('lang') || '').toLowerCase();
    return value === 'en' || value === 'lo' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Updates the URL with the specified language (English removes the parameter)
 * @param {string} lang - Language code ('en' or 'lo')
 * @param {Object} options - Options object
 * @param {boolean} options.replace - Use replaceState instead of pushState
 * @param {Location} location - The location object
 * @param {History} history - The history object
 */
export function updateUrlLang(
  lang,
  options = { replace: true },
  location = window.location,
  history = window.history
) {
  try {
    const url = new URL(location.href);
    // English is the default (and the only indexed) language, so keep its URLs clean
    if (lang === 'en') url.searchParams.delete('lang');
    else url.searchParams.set('lang', lang);
    if (options && options.replace) {
      history.replaceState({}, '', url.toString());
    } else {
      history.pushState({}, '', url.toString());
    }
  } catch {
    // ignore URL update failures
  }
}

/**
 * Checks if a URL is internal (relative) vs external
 * @param {string} href - The href to check
 * @returns {boolean} True if internal, false if external
 */
export function isInternalUrl(href) {
  if (!href) return false;
  if (href.startsWith('#')) return false;
  if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;
  if (href.startsWith('http://') || href.startsWith('https://')) return false;
  return true;
}

/**
 * Rewrites all internal links to include the specified language
 * @param {string} lang - Language code
 * @param {Document} doc - The document object
 * @param {Location} location - The location object
 */
export function rewriteInternalLinksForLang(lang, doc = document, location = window.location) {
  try {
    doc.querySelectorAll('a[href]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (!isInternalUrl(href)) return;
      try {
        const absolute = href.startsWith('/')
          ? new URL(href, location.origin)
          : new URL(href, location.href);
        if (lang === 'en') absolute.searchParams.delete('lang');
        else absolute.searchParams.set('lang', lang);
        const newHref = absolute.pathname + (absolute.search || '') + (absolute.hash || '');
        a.setAttribute('href', newHref);
      } catch {
        // skip invalid hrefs
      }
    });
  } catch {
    // ignore rewrite errors
  }
}

/**
 * Detects the initial language to use based on localStorage or browser settings
 * @param {Storage} storage - The storage object
 * @param {Navigator} nav - The navigator object
 * @returns {string} Language code ('en' or 'lo')
 */
export function detectInitialLang(storage = localStorage, nav = navigator) {
  const stored = storage.getItem('lang');
  if (stored) return stored;
  const navLangs = nav.languages || [nav.language || nav.userLanguage];
  const lc = (s) => String(s || '').toLowerCase();
  const foundLo = navLangs && navLangs.some((l) => lc(l).startsWith('lo'));
  if (foundLo) return 'lo';
  return 'en';
}

/**
 * Updates the language UI elements (flag icon and label)
 * @param {string} lang - Language code ('en' or 'lo')
 * @param {Document} doc - The document object
 */
export function setLangUI(lang, doc = document) {
  const label = doc.getElementById('langLabel') || doc.getElementById('langText');
  if (label) label.textContent = lang.toUpperCase();
  doc.querySelectorAll('img[data-flag]').forEach((img) => {
    img.setAttribute('src', lang === 'en' ? '/icons/flag-en.svg' : '/icons/flag-lo.svg');
  });
}
